import _ from 'lodash'
import dataProvider from 'service/data_provider'
import locationService from 'service/location'
import dispatcher from 'service/event_dispatcher'
import Controller from 'lib/controller'

import Router from 'service/router'

import ActivityGroupsCollection from 'bundle/activity_group/collection/activity_groups'
import ActivityGroupsView from 'bundle/activity_group/view/activity_groups'
import GroupOverviewSearchView from 'component/search_form/groups/groups'
import SidebarSearchView from 'component/sidebar_search/sidebar_search'

import windowView from 'shared/view/window'

import Collection from 'collection/collection'
import Model from 'model/model'
import SearchModel from 'component/search_form/model/search'

import InterestsView from 'component/interests/interests'

import FluidAdSlotView from 'view/fluid_ad_slot'

import SelectInterestTeaser from 'component/intents/select_interest_teaser'

const SearchResultsCollection = ActivityGroupsCollection.extend({
    url(...args) {
        // Replace with a reference to self when it exists on the SearchResults
        if (!this.length) {
            return Router.path('activity_group_api_activity_groups_index')
        }

        return ActivityGroupsCollection.prototype.url.apply(this, args)
    },
})

const GroupsOverviewPageState = Model.extend({
    parseEmbedded(embedded) {
        return {
            my: new ActivityGroupsCollection(embedded.my, { parse: true }),
            recommended: new ActivityGroupsCollection(embedded.recommended, { parse: true }),
            other: new ActivityGroupsCollection(embedded.other, { parse: true }),
            external: new ActivityGroupsCollection(embedded.external, { parse: true }),
            search: new SearchResultsCollection(embedded.search, { parse: true }),
            categories: new Collection(embedded.categories, { parse: true }),
        }
    },
})

const Ctrl = Controller.extend({
    _groupCollections: null,
    _isExternalVersion: false,

    initialize() {
        this._isExternalVersion = dataProvider.get('isExternalVersion')
        this.pageState = new GroupsOverviewPageState(dataProvider.get('activityGroups'), { parse: true })

        this.initSubview(FluidAdSlotView, {
            el: '.js-ad-slot-fluid',
        })

        this._initYourGroupsSection()
        this._initRecommendedGroupsSection()
        this._initOtherGroupsSection()
        this._initExternalGroupsSection()
        this._initSearchResultsSection()

        this._initInterestsModalHandler()
        this._initSidebar()

        this.listenTo(this.pageState, 'change:page', this._onPageStatePageChange)

        // Trigger a change on the initial load to set the appropriate page
        this.pageState.trigger('change:page')

        if (document.querySelector('.js-interests-teaser')) {
            this.initSubview(SelectInterestTeaser, { el: this.$('.js-interests-teaser') })
        }
    },

    bubbleEvents: {
        'SearchSidebar:search': '_onSidebarSearch',
        'SearchSidebar:reset': '_onSidebarReset',
    },

    _onSidebarSearch(searchModel) {
        if (!searchModel.hasValues()) {
            return
        }

        if (!this._isExternalVersion && !searchModel.attributes.term && !searchModel.attributes.categories) {
            locationService.clearQueryString()
            this.pageState.set('page', 'overview')
            return
        }

        if (this._isExternalVersion) {
            searchModel.set({ localcommunityId: dataProvider.get('localcommunityid') })
        }

        this.pageState.set('page', 'search')
        this._search(searchModel)
    },

    _onSidebarReset() {
        locationService.clearQueryString()
        this.pageState.set('page', 'overview')
    },

    _onDispatcherInterestsSync() {
        // Reload page as soon as user made adjustments to its interests.
        locationService.reload()
    },

    _onPageStatePageChange() {
        this._showPage(this.pageState.get('page'))
    },

    _initYourGroupsSection() {
        if (this.$('.js-your-groups').length === 0) {
            return
        }

        this.initSubview(ActivityGroupsView, {
            el: '.js-your-groups',
            collection: this.pageState.getEmbedded('my'),
            mode: 'manual',
            teaserImageLinkRef: 'gro_yg_im',
            teaserTitleLinkRef: 'gro_yg_ti',
        }).render()
    },

    _initRecommendedGroupsSection() {
        if (this.$('.js-recommended-groups').length === 0) {
            return
        }

        this.initSubview(ActivityGroupsView, {
            el: '.js-recommended-groups',
            collection: this.pageState.getEmbedded('recommended'),
            mode: 'manual',
            teaserImageLinkRef: 'gro_rg_im',
            teaserTitleLinkRef: 'gro_rg_ti',
        }).render()
    },

    _initOtherGroupsSection() {
        if (this.$('.js-other-groups').length === 0) {
            return
        }

        this.initSubview(ActivityGroupsView, {
            el: '.js-other-groups',
            collection: this.pageState.getEmbedded('other'),
            teaserImageLinkRef: 'gro_og_im',
            teaserTitleLinkRef: 'gro_og_ti',
            createGroupCTA: !this._isExternalVersion,
        }).render()
    },

    _initExternalGroupsSection() {
        if (this.$('.js-external-groups').length === 0) {
            return
        }

        this.initSubview(ActivityGroupsView, {
            el: '.js-external-groups',
            collection: this.pageState.getEmbedded('external'),
            teaserImageLinkRef: 'gro_eg_im',
            teaserTitleLinkRef: 'gro_eg_ti',
            createGroupCTA: this._isExternalVersion,
        }).render()
    },

    _initSearchResultsSection() {
        const searchView = this.initSubview(ActivityGroupsView, {
            collection: this.pageState.getEmbedded('search'),
            createGroupCTA: true,
            teaserImageLinkRef: 'gro_sr_im',
            teaserTitleLinkRef: 'gro_sr_ti',
        })

        searchView.render()

        this.$('.js-search-results').html(searchView.el)
    },

    _initInterestsModalHandler() {
        dispatcher.on('interests:redraw', initInterest, this)

        function initInterest(event, container) {
            this.initSubview(InterestsView, { el: container })
        }

        dispatcher.on('interests:sync', this._onDispatcherInterestsSync, this)
    },

    _initSidebar() {
        const searchModel = new SearchModel(locationService.getQueryStringAsObject())
        const pageState = this.pageState

        const SearchSidebarView = SidebarSearchView.extend({
            renderSearchForm() {
                const searchFormView = this.initSubview(GroupOverviewSearchView, {
                    categoryList: pageState.getEmbedded('categories'),
                    mobile: windowView.isMobile(),
                    model: searchModel,
                })

                searchFormView.render()

                return searchFormView
            },
        })

        const sidebarSearchView = this.initSubview(SearchSidebarView, {
            model: this.pageState,
            pageTitle: 'Groups',
        })

        sidebarSearchView.render()
        this.$('.js-sidebar-search').html(sidebarSearchView.el)

        if (searchModel.hasValues()) {
            if (searchModel.attributes.term || searchModel.attributes.categories) {
                if (searchModel.attributes.categories) {
                    searchModel.attributes.categories = [].concat(searchModel.attributes.categories)
                }
                this.pageState.set('page', 'search')
                this._search(searchModel)
            }
        }
    },

    _search(searchModel) {
        const { term, categories, localcommunityId } = searchModel.attributes
        const searchModelJSON = searchModel.toJSON()
        const undefinedKeys = Object.keys(searchModelJSON).filter(
            (key) => _.isUndefined(searchModelJSON[key]) || _.isNull(searchModelJSON[key])
        )
        const filteredSearchModel = _.omit(searchModelJSON, undefinedKeys)
        locationService.setQueryString({ term, categories, localcommunityId })
        this.pageState.getEmbedded('search').search(filteredSearchModel)
        this.$('.js-reset-search-form').toggleClass('is-hidden', false)
    },

    _showPage(page) {
        this.$('.js-page').toggleClass('is-hidden', true)
        this.$(`.js-page-${page}`).toggleClass('is-hidden', false)
    },
})

export default new Ctrl()
